const TOKEN_KEY = 'stories:token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  // El evento "storage" del navegador solo dispara en OTRAS pestanas, asi
  // que emitimos un evento propio para que los componentes de esta pestana
  // (FAB, useAuth, etc.) se enteren al instante del cambio.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('stories:token-changed', { detail: { token } }));
  }
}

async function request(method, path, body) {
  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (body) headers['Content-Type'] = 'application/json';

  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    if (res.status === 401) setToken(null);
    const message = (typeof data === 'object' && data?.error) || `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  return data;
}

async function rawRequest(method, path, body, contentType) {
  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (contentType) headers['Content-Type'] = contentType;

  const res = await fetch(`/api${path}`, { method, headers, body });

  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    if (res.status === 401) setToken(null);
    const message = (typeof data === 'object' && data?.error) || `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  health: () => request('GET', '/health'),

  auth: {
    check: (password, role = 'user') => request('POST', '/auth/check', { password, role }),
    redeemInvite: (code) => request('POST', '/auth/invite', { code }),
    me: () => request('GET', '/auth/me'),
  },

  stories: {
    options: () => request('GET', '/stories/options'),
    list: ({ page = 1, pageSize = 24 } = {}) =>
      request('GET', `/stories?page=${page}&pageSize=${pageSize}`),
    bySlug: (slug) => request('GET', `/stories/${encodeURIComponent(slug)}`),
    draft: (input) => request('POST', '/stories/draft', input),
    updateDraft: (id, patch) => request('PATCH', `/stories/${id}/draft`, patch),
    regenerateScene: (id, sceneIndex) =>
      request('POST', `/stories/${id}/regenerate-scene`, { sceneIndex }),
    generate: (id) => request('POST', `/stories/${id}/generate`),
    /**
     * Genera (o regenera) el audio TTS de una pagina concreta del cuento.
     *   part = 'cover'                    -> portada (titulo + sinopsis)
     *   part = { sceneIndex: number }     -> escena
     */
    generateAudio: (slug, part) => {
      const path = audioPathFor(slug, part);
      return request('POST', path);
    },
  },

  voice: {
    /**
     * Envia el blob de audio grabado en el navegador y devuelve
     * { transcript, fields, costs }.
     */
    dictate: ({ audioBlob, mimeType, languageCode = 'es-neutro', durationSeconds = 0 }) => {
      const qs = new URLSearchParams({
        languageCode,
        durationSeconds: String(durationSeconds),
      });
      return rawRequest('POST', `/voice/dictate?${qs.toString()}`, audioBlob, mimeType);
    },
  },

  jobs: {
    get: (id) => request('GET', `/jobs/${id}`),
  },

  admin: {
    listStories: ({ page = 1, pageSize = 50 } = {}) =>
      request('GET', `/admin/stories?page=${page}&pageSize=${pageSize}`),
    storyAction: (id, action, reason) =>
      request('PATCH', `/admin/stories/${id}`, { action, reason }),
    blacklist: () => request('GET', '/admin/blacklist'),
    addBlacklist: (word) => request('POST', '/admin/blacklist', { word }),
    removeBlacklist: (word) => request('DELETE', `/admin/blacklist/${encodeURIComponent(word)}`),
    moderationLog: (limit = 50) => request('GET', `/admin/moderation-log?limit=${limit}`),
    listInvites: () => request('GET', '/admin/invites'),
    createInvites: ({ label, count = 1 } = {}) => request('POST', '/admin/invites', { label, count }),
    deleteInvite: (code) => request('DELETE', `/admin/invites/${encodeURIComponent(code)}`),

    /** Devuelve { blob, filename, stories, images, audio } para descargar. */
    exportStories: async (ids) => {
      const token = getToken();
      const res = await fetch('/api/admin/export', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const ct = res.headers.get('content-type') ?? '';
        const data = ct.includes('application/json') ? await res.json() : await res.text();
        const msg = (typeof data === 'object' && data?.error) || `HTTP ${res.status}`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      return {
        blob,
        filename: m?.[1] || 'stories-export.db',
        stories: Number(res.headers.get('x-export-stories') ?? 0),
        images: Number(res.headers.get('x-export-images') ?? 0),
        audio: Number(res.headers.get('x-export-audio') ?? 0),
      };
    },

    /** Sube un fichero .db y devuelve el plan/resultado del merge. */
    importStories: async (file, { dryRun = false, overwrite = false } = {}) => {
      const qs = new URLSearchParams();
      if (dryRun) qs.set('dryRun', '1');
      if (overwrite) qs.set('overwrite', '1');
      const token = getToken();
      const res = await fetch(`/api/admin/import?${qs.toString()}`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/octet-stream',
        },
        body: file,
      });
      const ct = res.headers.get('content-type') ?? '';
      const data = ct.includes('application/json') ? await res.json() : await res.text();
      if (!res.ok) {
        const msg = (typeof data === 'object' && data?.error) || `HTTP ${res.status}`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
      }
      return data;
    },
  },
};

/**
 * Construye la ruta de audio para una pagina concreta del cuento.
 *   part = 'cover'                -> /api/stories/:slug/audio/cover
 *   part = { sceneIndex: number } -> /api/stories/:slug/audio/scene/:index
 */
function audioPathFor(slug, part) {
  const slugEnc = encodeURIComponent(slug);
  if (part === 'cover') return `/stories/${slugEnc}/audio/cover`;
  if (part && typeof part.sceneIndex === 'number') {
    return `/stories/${slugEnc}/audio/scene/${part.sceneIndex}`;
  }
  throw new Error(`audioPathFor: parte invalida (${JSON.stringify(part)})`);
}

export function audioUrl(slug, part) {
  if (!slug || !part) return null;
  return '/api' + audioPathFor(slug, part);
}

/**
 * Decodifica el payload del token (sin verificar firma — solo lectura local).
 */
export function decodeTokenRole(token) {
  if (!token || !token.includes('.')) return null;
  try {
    const body = token.split('.')[0];
    const json = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')));
    return json.role ?? null;
  } catch {
    return null;
  }
}

export function imageUrl(imageId) {
  return imageId ? `/api/images/${encodeURIComponent(imageId)}` : null;
}

/**
 * Suscripcion SSE al progreso de un job.
 *   onEvent({ event: 'snapshot'|'progress'|'done'|'failed', data })
 * Devuelve una funcion para cerrar la conexion.
 */
export function subscribeJob(jobId, onEvent) {
  const token = getToken() ?? '';
  const url = `/api/jobs/${encodeURIComponent(jobId)}/events?token=${encodeURIComponent(token)}`;
  const es = new EventSource(url);

  const handle = (name) => (e) => {
    let parsed;
    try {
      parsed = JSON.parse(e.data);
    } catch {
      parsed = e.data;
    }
    onEvent({ event: name, data: parsed });
  };

  es.addEventListener('snapshot', handle('snapshot'));
  es.addEventListener('progress', handle('progress'));
  es.addEventListener('status', handle('progress'));
  es.addEventListener('done', (e) => {
    handle('done')(e);
    es.close();
  });
  es.addEventListener('failed', (e) => {
    handle('failed')(e);
    es.close();
  });
  es.onerror = () => {
    onEvent({ event: 'error', data: { message: 'connection error' } });
  };

  return () => es.close();
}
